import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminHackCreateAction,
  adminHackGetAction,
  adminHackUpdateAction,
  adminHackWeekOccupiedDaysAction,
  adminHackWeekSelectOptionsAction
} from "../Action/action";
import config from "../coreFIles/config";
import CKEditorWithSource from "./CKEditorWithSource";
import axios from "axios";
import { getAdminAuthToken } from "../coreFIles/auth";

const categories = ["Mindfulness", "Cognitive", "Parenting", "Emotional", "Confidence", "Social Skills"];

const DAYS_1_7 = [1, 2, 3, 4, 5, 6, 7];

function normalizeStatusFromHack(h) {
  const active = h?.active !== undefined && h?.active !== null ? Number(h.active) : null;
  if (active === 1) return "Published";
  const pd = h?.publish_date || null;
  if (pd) {
    const t = new Date(pd);
    if (!Number.isNaN(t.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      t.setHours(0, 0, 0, 0);
      if (t < today) return "Archived";
    }
  }
  return "Draft";
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function editorPlainText(html) {
  return String(html || "")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);

    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });

    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");

    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function CoachingHackForm({ mode }) {
  const { hackId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loadingHack, setLoadingHack] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("Draft");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState("all");
  const [hackWeekId, setHackWeekId] = useState("");
  const [hackDay, setHackDay] = useState("");
  const [weekOptions, setWeekOptions] = useState([]);
  const [occupiedDays, setOccupiedDays] = useState([]);
  const [occupancyLoading, setOccupancyLoading] = useState(false);

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState("");

  useEffect(() => {
    if (!thumbnailFile) {
      if (thumbnailPreviewUrl) setThumbnailPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreviewUrl(url);
    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailFile]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminHackWeekSelectOptionsAction();
        if (cancelled || !res?.success) return;
        setWeekOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setWeekOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (visibility !== "purchasers" || !String(hackWeekId || "").trim()) {
      setOccupiedDays([]);
      setOccupancyLoading(false);
      return undefined;
    }
    let cancelled = false;
    setOccupancyLoading(true);
    const excludeId = mode === "edit" && hackId ? hackId : undefined;
    (async () => {
      try {
        const res = await adminHackWeekOccupiedDaysAction(hackWeekId, excludeId);
        if (cancelled) return;
        const list = res?.data?.occupied_days;
        setOccupiedDays(Array.isArray(list) ? list.map(String) : []);
      } catch (e) {
        if (!cancelled) setOccupiedDays([]);
      } finally {
        if (!cancelled) setOccupancyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visibility, hackWeekId, mode, hackId]);

  useEffect(() => {
    if (visibility !== "purchasers" || !hackDay) return;
    if (occupiedDays.includes(String(hackDay))) setHackDay("");
  }, [visibility, occupiedDays, hackDay]);

  useEffect(() => {
    let cancelled = false;
    async function loadHack() {
      if (mode !== "edit") return;
      setLoadingHack(true);
      setError("");
      try {
        const res = await adminHackGetAction(hackId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || res?.msg || "Failed to load hack.");
          return;
        }
        const h = res.data || {};
        setTitle(h.title || "");
        setCategory(h.category || "");
        setStatus(normalizeStatusFromHack(h));
        setShortDescription(h.short_content || "");
        setDescription(h.content || "");
        setVisibility(h.visibility === "purchasers" ? "purchasers" : "all");
        setHackWeekId(h.week != null && h.week !== "" ? String(h.week) : "");
        const dn = parseInt(String(h.day ?? "").replace(/\D/g, ""), 10);
        setHackDay(Number.isFinite(dn) && dn >= 1 && dn <= 7 ? String(dn) : "");
        setExistingThumbnailUrl(resolvePublicUrl(h.thumbnail_url));
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load hack.");
      } finally {
        if (!cancelled) setLoadingHack(false);
      }
    }
    loadHack();
    return () => {
      cancelled = true;
    };
  }, [hackId, mode]);

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = "Title is required.";
    if (!String(category || "").trim()) errs.category = "Category is required.";
    if (!editorPlainText(shortDescription)) errs.shortDescription = "Short Description is required.";
    if (!editorPlainText(description)) errs.description = "Description is required.";
    if (visibility === "purchasers") {
      if (!String(hackWeekId || "").trim()) errs.hackWeek = "Course week is required.";
      if (!String(hackDay || "").trim()) errs.hackDay = "Day is required.";
      else {
        const n = parseInt(String(hackDay), 10);
        if (!Number.isFinite(n) || n < 1 || n > 7) errs.hackDay = "Day must be from 1 to 7.";
        else if (occupiedDays.includes(String(n))) errs.hackDay = "This day is already used for the selected week.";
      }
    }
    return errs;
  };

  const publishDateForStatus = (nextStatus) => {
    if (nextStatus === "Archived") {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d.toISOString().slice(0, 10);
    }
    // Clear publish_date for Draft/Published so archived filter doesn't stick.
    return "";
  };

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the required fields.", { toastId: "hack-save" });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const nextStatus = status || "Draft";
      const active = nextStatus === "Published" ? 1 : 0;

      const fd = new FormData();
      fd.append("title", String(title).trim());
      fd.append("category", String(category).trim());
      fd.append("short_content", shortDescription || "");
      fd.append("content", description || "");
      fd.append("active", String(active));
      fd.append("visibility", visibility);
      fd.append("publish_date", publishDateForStatus(nextStatus));
      fd.append("week", visibility === "purchasers" ? String(hackWeekId || "") : "");
      fd.append("day", visibility === "purchasers" ? String(hackDay || "") : "");
      if (thumbnailFile) fd.append("thumbnail", thumbnailFile);

      const res =
        mode === "edit"
          ? await adminHackUpdateAction(hackId, fd)
          : await adminHackCreateAction(fd);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Save failed");

      toast.success(mode === "edit" ? "Hack updated" : "Hack created", { toastId: "hack-save" });
      navigate("/coaching-hacks");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "hack-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {mode === "edit" && loadingHack ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save hack.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/coaching-hacks">Coaching Hacks</Link>
          <span>›</span>
          <span>{mode === "edit" ? "Edit Hack" : "Add New Hack"}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/coaching-hacks" className="btn btn-outline btn-sm">
            ← Back
          </Link>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="card mb-6">
            <h2 className="section-title">Hack Details</h2>

            <div className="form-group">
              <label>
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter coaching hack title..."
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors((p) => ({ ...p, title: "" }));
                }}
                aria-invalid={!!fieldErrors.title}
                style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
              />
              {fieldErrors.title ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.title}
                </div>
              ) : null}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setFieldErrors((p) => ({ ...p, category: "" }));
                  }}
                  aria-invalid={!!fieldErrors.category}
                  style={fieldErrors.category ? { borderColor: "var(--danger)" } : undefined}
                >
                  <option value="">Select category...</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                {fieldErrors.category ? (
                  <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                    {fieldErrors.category}
                  </div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Status</label>
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option>Draft</option>
                  <option>Published</option>
                  <option>Archived</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>
                Short Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-200"
                containerStyle={
                  fieldErrors.shortDescription
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={shortDescription}
                onChange={(html) => {
                  setShortDescription(html);
                  setFieldErrors((p) => ({ ...p, shortDescription: "" }));
                }}
              />
              {fieldErrors.shortDescription ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.shortDescription}
                </div>
              ) : null}
            </div>

            <div className="form-group">
              <label>
                Description <span className="text-danger">*</span>
              </label>
              <CKEditorWithSource
                heightClassName="ckeditor-height-400"
                containerStyle={
                  fieldErrors.description
                    ? { border: "1px solid var(--danger)", borderRadius: 6, overflow: "hidden" }
                    : { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }
                }
                config={{ extraPlugins: [CkUploadAdapterPlugin] }}
                data={description}
                onChange={(html) => {
                  setDescription(html);
                  setFieldErrors((p) => ({ ...p, description: "" }));
                }}
              />
              {fieldErrors.description ? (
                <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                  {fieldErrors.description}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div>
          <div className="card mb-6">
            <h2 className="section-title">Thumbnail Image</h2>
            <div
              className="upload-area p-sm"
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click?.()}
              onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click?.()}
            >
              <div className="avatar-upload" style={{ overflow: "hidden" }}>
                {thumbnailPreviewUrl || existingThumbnailUrl ? (
                  <img
                    src={thumbnailPreviewUrl || existingThumbnailUrl}
                    alt="Thumbnail preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span style={{ color: "var(--primary)", display: "inline-flex" }} aria-hidden="true">
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="9" cy="9" r="2.5" />
                      <path d="M3 16l5-5 4 4 3-3 6 6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className="font-sm">
                <strong>Click to upload</strong> or drag and drop
              </p>
              <p className="small-label">PNG, JPG up to 2MB</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setThumbnailFile(f);
              }}
            />
          </div>

          <div className="card mb-6">
            <h2 className="section-title">Publishing</h2>
            <div className="form-group">
              <label>Visibility</label>
              <select
                className="form-control"
                value={visibility}
                onChange={(e) => {
                  const v = e.target.value;
                  setVisibility(v);
                  setFieldErrors((p) => ({ ...p, hackWeek: "", hackDay: "" }));
                  if (v !== "purchasers") {
                    setHackWeekId("");
                    setHackDay("");
                  }
                }}
              >
                <option value="all">All Users</option>
                <option value="purchasers">Course Purchasers Only</option>
              </select>
            </div>
            {visibility === "purchasers" ? (
              <>
                <div className="form-group">
                  <label>
                    Course week <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={hackWeekId}
                    onChange={(e) => {
                      setHackWeekId(e.target.value);
                      setHackDay("");
                      setFieldErrors((p) => ({ ...p, hackWeek: "", hackDay: "" }));
                    }}
                    aria-invalid={!!fieldErrors.hackWeek}
                    style={fieldErrors.hackWeek ? { borderColor: "var(--danger)" } : undefined}
                  >
                    <option value="">Select course week…</option>
                    {weekOptions.map((w) => (
                      <option key={w.id} value={String(w.id)}>
                        {(w.course_title || `Course ${w.course_id ?? ""}`).trim()} — {w.title || `Week ${w.id}`}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.hackWeek ? (
                    <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                      {fieldErrors.hackWeek}
                    </div>
                  ) : null}
                </div>
                <div className="form-group">
                  <label>
                    Day (1–7) <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={hackDay}
                    disabled={occupancyLoading}
                    onChange={(e) => {
                      setHackDay(e.target.value);
                      setFieldErrors((p) => ({ ...p, hackDay: "" }));
                    }}
                    aria-invalid={!!fieldErrors.hackDay}
                    style={fieldErrors.hackDay ? { borderColor: "var(--danger)" } : undefined}
                  >
                    <option value="">{occupancyLoading ? "Loading days…" : "Select day…"}</option>
                    {DAYS_1_7.map((d) => {
                      const key = String(d);
                      const taken = occupiedDays.includes(key);
                      return (
                        <option key={d} value={key} disabled={taken}>
                          {d}
                          {taken ? " (already used)" : ""}
                        </option>
                      );
                    })}
                  </select>
                  {fieldErrors.hackDay ? (
                    <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                      {fieldErrors.hackDay}
                    </div>
                  ) : (
                    <p className="small-label" style={{ marginTop: 6 }}>
                      One hack per day per course week. Days taken by other hacks are disabled.
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </div>

          <div className="card">
            <h2 className="section-title">Summary</h2>
            <div className="summary-list">
              <div className="flex justify-between">
                <span>Status:</span>{" "}
                <span className={`badge ${status === "Draft" ? "badge-warning" : status === "Published" ? "badge-success" : "badge-gray"}`}>
                  {status}
                </span>
              </div>
              <div className="flex justify-between mt-4">
                <span>Category:</span> <span>{category || "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => onSave()}
          disabled={
            saving ||
            loadingHack ||
            (visibility === "purchasers" &&
              String(hackWeekId || "").trim() !== "" &&
              occupancyLoading)
          }
        >
          {saving ? (
            "Saving…"
          ) : (
            <>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
                <path d="M17 21v-8H7v8" />
                <path d="M7 3v5h8" />
              </svg>
              Save Hack
            </>
          )}
        </button>
        <Link to="/coaching-hacks" className="btn btn-outline">
          Cancel
        </Link>
      </div>
    </div>
  );
}

